import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * Les utilitaires typographiques maison (`text-payfit-label`, `-body`…, cf.
 * src/index.css) commencent par `text-` : tailwind-merge les prenait pour des
 * couleurs de texte, si bien qu'un utilitaire placé après une couleur l'effaçait.
 * Badge perdait ainsi la couleur de chaque variante (gris sur fond rouge pour
 * `destructive`). Ils ont leur propre groupe, et le conflit ne joue que dans un
 * sens : une couleur écrite après un utilitaire le remplace toujours (rendu
 * inchangé partout ailleurs), un utilitaire écrit après une couleur la conserve —
 * l'ordre CSS de Tailwind fait alors gagner la couleur.
 */
const twMerge = extendTailwindMerge<"payfit-type">({
  extend: {
    classGroups: {
      "payfit-type": [
        { "text-payfit": ["h1", "h2", "h3", "label", "field-label", "value", "nav", "body", "muted", "placeholder"] },
      ],
    },
    conflictingClassGroups: {
      "text-color": ["payfit-type"],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Convert decimal hours to a human-readable format
 * @example formatHoursMinutes(7.67) => "7h40"
 * @example formatHoursMinutes(5.5) => "5h30"
 * @example formatHoursMinutes(8) => "8h"
 */
export function formatHoursMinutes(decimalHours: number): string {
  let hours = Math.floor(Math.abs(decimalHours));
  let minutes = Math.round((Math.abs(decimalHours) - hours) * 60);
  // Math.round can produce 60 due to floating-point imprecision (e.g. 38.999… → 38h60)
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }
  const sign = decimalHours < 0 ? "-" : "";
  if (minutes === 0) return `${sign}${hours}h`;
  return `${sign}${hours}h${minutes.toString().padStart(2, "0")}`;
}

/**
 * Convert minutes to a human-readable format
 * @example formatMinutes(140) => "2h20"
 * @example formatMinutes(60) => "1h"
 */
export function formatMinutes(totalMinutes: number): string {
  const rounded = Math.round(Math.abs(totalMinutes));
  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;
  const sign = totalMinutes < 0 ? "-" : "";
  if (hours === 0) return `${sign}${minutes}min`;
  if (minutes === 0) return `${sign}${hours}h`;
  return `${sign}${hours}h${minutes.toString().padStart(2, "0")}`;
}
